import styled from 'styled-components';
import { CheckmarkIc, RemoveIc } from '@dsch/dd-icons';

import { useUserContext } from 'contexts/UserContext';
import { media } from 'helpers/breakpoints';
import { WhitePanel } from 'components/Toolkit//WhitePanel/WhitePanel';
import { DetailPanel } from 'components/Toolkit/DetailPanel/DetailPanel';
import { Button } from 'components/Toolkit/Button/Button';

export interface GreenlightPanelProps {
  listBlock?: ListBlock;
  titleGroup: TitleGroup;
  infoLink?: InfoLink;
  greenlightVerified?: boolean;
  showHistoryCheck?: boolean;
  className?: string;
  cdnUrl?: string;
}

export interface ListBlock {
  items: ListItem[];
  extraInfo?: { text: string };
}

export interface ListItem {
  icon?: string;
  body?: string;
  title?: string;
}

export interface TitleGroup {
  icon?: string;
  title?: string;
  subtitle?: string;
}

export interface InfoLink {
  external?: boolean;
  text?: string;
  url?: string;
}

const GreenlightWhitePanel = styled(WhitePanel)<{ extraInfo: boolean }>`
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.default} ${theme.borderRadius.default} 0 0`};
  ${media.large} {
    ${({ theme, extraInfo }) =>
      extraInfo && `padding-bottom: ${theme.spacing.M16}`};
  }
`;

const SDetailPanel = styled(DetailPanel)<{ extraInfo: boolean }>`
  margin-bottom: ${({ theme, extraInfo }) => extraInfo && theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme, extraInfo }) => extraInfo && theme.spacing.M24};
  }
`;

const ExtraInfo = styled.p`
  ${({ theme }) => theme.fontSize.B14};
`;

const HistoryCheckWhitePanel = styled.div`
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.M24};
`;

const SCheckmarkIc = styled(CheckmarkIc)`
  color: ${({ theme }) => theme.colors.GREEN};
`;

const SIoRemove = styled(RemoveIc)`
  color: ${({ theme }) => theme.colors.GREEN};
`;

const NeutralIoRemove = styled(RemoveIc)`
  color: ${({ theme }) => theme.colors.GREY};
`;

function iconSelector(iconType: string | undefined) {
  switch (iconType) {
    case 'icon-check-positive-tick':
      return (
        <SCheckmarkIc width="16" height="16" data-testid="checkmark-icon" />
      );
    case 'icon-check-positive-dash':
      return (
        <SIoRemove width="16" height="16" data-testid="positive-remove-icon" />
      );
    default:
      return (
        <NeutralIoRemove
          width="16"
          height="16"
          data-testid="neutral-remove-icon"
        />
      );
  }
}

function GreenlightPanel({
  titleGroup,
  infoLink,
  listBlock,
  greenlightVerified,
  showHistoryCheck,
  className,
  cdnUrl,
}: GreenlightPanelProps) {
  const { setIsHistoryCheckModalOpen } = useUserContext();
  const filteredValues = listBlock?.items.map((item) => {
    return {
      title: item.title,
      body: item.body,
      icon: iconSelector(item.icon),
    };
  });

  return (
    <div className={className}>
      <GreenlightWhitePanel extraInfo={Boolean(listBlock?.extraInfo)}>
        {titleGroup.title && filteredValues && (
          <SDetailPanel
            title={titleGroup.title}
            illustrationUrl={`${cdnUrl}/images/illustrations/greenlight${
              !greenlightVerified ? '-unverified' : ''
            }.svg`}
            detailListItems={filteredValues}
            listItems={[titleGroup.subtitle]}
            link={infoLink}
            extraInfo={Boolean(listBlock?.extraInfo)}
          />
        )}
        {listBlock?.extraInfo && (
          <ExtraInfo>{listBlock.extraInfo.text}</ExtraInfo>
        )}
        {showHistoryCheck && (
          <HistoryCheckWhitePanel>
            <Button
              onClick={() => setIsHistoryCheckModalOpen(true)}
              ofType="SECONDARY"
            >
              Get full history for €18
            </Button>
          </HistoryCheckWhitePanel>
        )}
      </GreenlightWhitePanel>
    </div>
  );
}

export { GreenlightPanel };
